import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { firstValueFrom } from 'rxjs';

import { CustomCurvesQuery } from '@models/custom-curves/custom-curves.query';
import { CustomCurve, CustomCurvesStore } from '@models/custom-curves/custom-curves.state';

import { CustomValidators } from '@shared/components/base-form-control/custom-validators';
import { InputComponent } from '@shared/components/input/input.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { CurveType, CustomType, DriverType, GqlService } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';

import { CustomDriverType, DistributionType } from '../../constants/custom-curves.constants';

export interface CreateEditCustomDriverInputData {
  existingNames: string[];
  id?: string;
  name?: string;
  type?: CustomType;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [InputComponent, NgSelectModule, ReactiveFormsModule],
  selector: 'aux-custom-driver-create-edit-modal',
  templateUrl: './custom-driver-create-edit-modal.component.html',
})
export class CustomDriverCreateEditModalComponent implements OnInit {
  ref = inject(CustomOverlayRef<CustomCurve, CreateEditCustomDriverInputData>);

  private readonly customCurvesQuery = inject(CustomCurvesQuery);
  private readonly customCurvesStore = inject(CustomCurvesStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly gqlService = inject(GqlService);
  private readonly overlayService = inject(OverlayService);

  allowChangeType = signal(false);
  fetchingDistributions = signal(true);
  initialName = signal('');
  saving = signal(false);

  distributionTypes = Object.values(DistributionType);
  nameControl = new FormControl<string>('');
  nameValidators = [];
  selectedType = new FormControl<null | string>(null, Validators.required);

  private readonly disableTypeEdit = effect(() => {
    if (!!this.initialName() && !this.allowChangeType()) {
      this.selectedType.disable();
    } else {
      this.selectedType.enable();
    }
  });

  ngOnInit(): void {
    if (this.ref.data?.id) {
      this.gqlService
        .listDriverCustomDistribution$(this.ref.data.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((distributions) => {
          const existingDistributions = distributions.data || [];

          this.allowChangeType.set(
            existingDistributions.every((distribution) => distribution.custom_amount == 0) ||
              !existingDistributions.length
          );
          this.fetchingDistributions.set(false);
        });
    }

    if (this.ref.data?.name) {
      this.initialName.set(this.ref.data.name);
    }

    if (this.ref.data?.type) {
      const getDistributionLabel = (object: { [key: string]: string }, value: string) => {
        return Object.keys(object).find((key) => object[key] === value);
      };

      this.selectedType.setValue(
        getDistributionLabel(CustomDriverType, this.ref.data.type) as string
      );
    }

    this.nameControl.setValue(this.initialName());
  }

  private get existingNames(): string[] {
    const names = this.ref?.data?.existingNames || [];
    const driverName = this.ref?.data?.name;

    return driverName ? names.filter((name) => name !== driverName) : names;
  }

  get name(): string | undefined {
    return this.ref?.data?.name;
  }

  get nameControlValidators(): ValidatorFn[] {
    return [
      Validators.required,
      CustomValidators.valueNotInList(this.existingNames, 'duplicatedCustomCurveName'),
    ];
  }

  async onCreate(): Promise<void> {
    this.saving.set(true);
    const resp = await firstValueFrom(
      this.gqlService.createCustomGroup$({
        name: this.nameControl.value as string,
        rank_order: 0,
        type: CustomDriverType[this.selectedType.value as string],
      })
    );

    if (resp.errors?.length) {
      this.overlayService.error(resp.errors);
      this.saving.set(false);

      return;
    }

    if (!resp.data || !resp.success) {
      return;
    }

    const settingsResp = await firstValueFrom(
      this.gqlService.createDriverSetting$({
        custom_group_id: resp.data.id,
        driver_type: DriverType.DRIVER_CUSTOM,
      })
    );

    if (settingsResp.errors?.length) {
      this.overlayService.error(settingsResp.errors);
      this.saving.set(false);

      return;
    }

    const curveToCreate = {
      curve_type: CurveType.NET,
      custom_group_id: resp.data.id,
      custom_type: resp.data.type,
      driver_setting_id: settingsResp.data?.id,
      name: resp.data.name,
    };

    this.customCurvesStore.updatePartialy({ curveToCreateOrEdit: curveToCreate });
    this.overlayService.success(MessagesConstants.CUSTOM_CURVES.SUCCESSFULLY_CREATED);
    this.ref.close(curveToCreate);
  }

  async onSave(): Promise<void> {
    if (this.nameControl.invalid || this.selectedType.invalid) {
      return;
    }

    if (this.ref.data?.name) {
      await this.onUpdate();

      return;
    }

    await this.onCreate();
  }

  async onUpdate(): Promise<void> {
    this.saving.set(true);
    const resp = await firstValueFrom(
      this.gqlService.updateCustomGroup$({
        id: this.ref.data?.id as string,
        name: this.nameControl.value as string,
        rank_order: 0,
        type: CustomDriverType[this.selectedType.value as string],
      })
    );

    if (resp.errors?.length) {
      this.overlayService.error(resp.errors);
      this.saving.set(false);

      return;
    }

    const elementToEdit = this.customCurvesQuery
      .curves()
      .find((curve) => curve.custom_group_id === this.ref.data?.id);

    this.customCurvesStore.updatePartialy({
      curveToCreateOrEdit: {
        ...elementToEdit,
        custom_type: resp.data?.type,
        name: resp.data?.name,
      },
    });
    this.overlayService.success(`${resp.data?.name} successfully updated!`);
    this.ref.close();
  }

  get saveDisabled(): boolean {
    return this.nameControl.invalid || this.selectedType.invalid || this.saving();
  }
}
